import React, { createContext, useContext, useMemo } from "react";
import type { AuthWrapperConfig } from "../types";
import { buildApiEndpoints, type ApiEndpoints } from "./defaults";

export interface ResolvedAuthConfig extends AuthWrapperConfig {
  endpoints: ApiEndpoints;
}

const AuthConfigContext = createContext<ResolvedAuthConfig | null>(null);

let globalConfig: ResolvedAuthConfig | null = null;

interface AuthConfigProviderProps {
  config: AuthWrapperConfig;
  children: React.ReactNode;
}

export function AuthConfigProvider({
  config,
  children,
}: AuthConfigProviderProps) {
  const resolved = useMemo<ResolvedAuthConfig>(() => {
    const resolvedConfig: ResolvedAuthConfig = {
      ...config,
      endpoints: buildApiEndpoints(config.apiUrls),
    };
    globalConfig = resolvedConfig;
    return resolvedConfig;
  }, [config]);

  return (
    <AuthConfigContext.Provider value={resolved}>
      {children}
    </AuthConfigContext.Provider>
  );
}

export function useAuthConfig(): ResolvedAuthConfig {
  const config = useContext(AuthConfigContext);
  if (!config) {
    throw new Error("useAuthConfig must be used within an AuthWrapper");
  }
  return config;
}

/**
 * Get config from outside React component tree (services, thunks).
 * Returns null if not yet initialized.
 */
export function getAuthConfig(): ResolvedAuthConfig | null {
  return globalConfig;
}

/**
 * Get resolved API endpoints from outside React component tree.
 * Falls back to default RISA URLs if config not yet initialized.
 */
export function getApiEndpoints(): ApiEndpoints {
  return globalConfig?.endpoints ?? buildApiEndpoints();
}
